import React, { useState, useRef, useEffect } from "react";
import Modal from "react-modal";
import { FaFilePdf, FaTimes, FaFileVideo, FaPaperclip } from "react-icons/fa";

Modal.setAppElement("#root");

const AttachmentButton = ({ attach, onOpen }) => {
  if (!attach) return null;

  const fileName = attach.basename || attach.name || attach.filename || "Dosya";
  const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";
  const ctype = attach.CTYPE ? attach.CTYPE.toLowerCase() : fileExtension;

  let type = ctype;
  const videoTypes = ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm', 'flv'];
  const imageTypes = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'];
  if (videoTypes.includes(type)) type = 'video';
  if (imageTypes.includes(type)) type = 'image';

  const iconMap = {
    pdf: <FaFilePdf className="text-slate-100" />,
    video: <FaFileVideo className="text-slate-100" />,
    image: <FaPaperclip className="text-slate-100" />,
    default: <FaPaperclip className="text-slate-100" />,
  };

  const icon = iconMap[type] || iconMap.default;

  return (
    <button
      onClick={() => onOpen(type, attach.URL)}
      className="inline-flex items-center gap-1 rounded bg-white/12 px-1.5 py-0.5 text-[9px] font-medium text-slate-100 transition hover:bg-white/20"
    >
      {icon}
      <span className="max-w-[120px] truncate text-left">{fileName}</span>
    </button>
  );
};

const FormView = ({ formData, flowEvents = [] }) => {
  const containerRef = useRef(null);
  const [modalContent, setModalContent] = useState({ type: "", url: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [formData]);

  if (!formData || Object.keys(formData).length === 0) {
    return <p className="text-sm italic text-slate-300">Veri bulunamadı.</p>;
  }

  const openModal = (type, url) => {
    setModalContent({ type, url });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent({ type: "", url: "" });
  };

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, index) => (
            <AttachmentButton key={`${item?.ATTID || index}`} attach={item} onOpen={openModal} />
          ))}
        </div>
      );
    }

    if (value && typeof value === 'object') {
      return (
        <pre className="max-h-32 overflow-auto rounded-lg bg-black/20 px-2 py-1.5 text-[10px] text-blue-100">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    return (
      <span className="break-words text-[10px] leading-relaxed text-slate-100">
        {value === null || value === undefined || value === '' ? '-' : String(value)}
      </span>
    );
  };

  return (
    <div ref={containerRef} className="text-slate-100">
      {Object.entries(formData).map(([sectionKey, sectionValue]) => {
        if (!sectionValue) return null;

        const entries = Array.isArray(sectionValue) ? sectionValue : [sectionValue];

        return (
          <div key={sectionKey}>
            {entries.map((entry, idx) =>
              typeof entry === 'object' ? (
                <dl key={`${sectionKey}-${idx}`} className="divide-y divide-white/[0.06]">
                  {Object.entries(entry).map(([fieldKey, fieldValue]) => (
                    <div key={fieldKey} className="flex items-baseline gap-3 py-1.5">
                      <dt className="w-24 flex-shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                        {fieldKey}
                      </dt>
                      <dd className="flex-1 min-w-0">
                        {renderValue(fieldValue)}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <div key={`${sectionKey}-${idx}`} className="py-1.5">
                  {renderValue(entry)}
                </div>
              )
            )}
          </div>
        );
      })}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        className="bg-[#101b30] text-slate-100 rounded-2xl shadow-2xl w-full max-w-5xl mx-auto"
        overlayClassName="fixed inset-0 bg-black/70 flex items-center justify-center"
      >
        <header className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.28em] text-blue-100/80">
            Ek Önizleme
          </h2>
          <button
            onClick={closeModal}
            className="rounded-full border border-white/15 p-1.5 text-sm text-slate-200 transition hover:border-white/35 hover:bg-white/10"
          >
            <FaTimes />
          </button>
        </header>
        <div className="max-h-[70vh] overflow-auto px-5 py-4">
          {modalContent.type === "video" && (
            <video
              controls
              src={modalContent.url}
              className="h-[420px] w-full rounded-xl bg-black/40 object-contain"
            />
          )}
          {modalContent.type === "pdf" && (
            <iframe
              src={modalContent.url}
              title="PDF Preview"
              className="h-[420px] w-full rounded-xl bg-white"
            />
          )}
          {modalContent.type === "image" && (
            <img
              src={modalContent.url}
              alt="Preview"
              className="max-h-[420px] w-full rounded-xl object-contain"
            />
          )}
          {modalContent.type !== "video" &&
            modalContent.type !== "pdf" &&
            modalContent.type !== "image" && (
              <div className="flex h-[420px] items-center justify-center rounded-xl bg-white/8 text-sm text-slate-200">
                Önizleme desteklenmiyor.
              </div>
            )}
        </div>
      </Modal>
    </div>
  );
};

export default FormView;
